import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

/** Class names, falsy ones left out. Tailwind classes only, from the shared tokens. */
export function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(' ');
}

/** Every target is at least 44 px, with a focus ring that survives `outline: none`. */
export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

const VARIANTS = {
  primary:
    'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-60',
  outline: 'border border-input bg-card text-heading hover:border-primary hover:bg-accent',
  link: 'text-primary underline underline-offset-4 hover:no-underline',
} as const;

export function FormButton({
  variant = 'primary',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof VARIANTS;
}) {
  return (
    <button
      type="button"
      className={cx(
        'inline-flex min-h-12 items-center justify-center gap-2 rounded-lg px-5 text-base font-semibold transition-colors',
        'disabled:cursor-not-allowed',
        FOCUS_RING,
        VARIANTS[variant],
        variant === 'link' && 'min-h-[2.75rem] px-1',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** An error tied to its field: announced by the field's `aria-describedby`. */
export function FieldError({ id, lang, children }: { id: string; lang?: string; children: ReactNode }) {
  return (
    <p id={id} lang={lang} className="mt-2 flex items-start gap-1.5 text-base font-medium text-danger">
      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}
